import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useSessionState } from '@apps/web/context/session_state_provider';
import { registerAgentConversationOpener } from '@apps/web/lib/active_agent_conversation';
import {
    type AgentRunSummary,
    createAgentChatDestinationResolver,
    normalizeAgentChatSearch,
} from '@apps/web/lib/agent_chat_navigation';
import { type ChatComposerDraft } from '@apps/web/lib/chat_composer_draft';
import {
    type ChatPanelMode,
    readPersistedChatVisible,
    writePersistedChatVisible,
} from '@apps/web/lib/chat_ui_persistence';
import { modeStore } from '@apps/web/lib/mode_store';
import { type Mode } from '@libraries/typescript/api_client';

const ORGANIZATION_STORAGE_KEY_PREFIX = 'phoebe-organization-id';

function readSelectedOrganizationId(userId: string | null): string | null {
    if (userId === null || typeof window === 'undefined') {
        return null;
    }
    try {
        return window.sessionStorage.getItem(
            `${ORGANIZATION_STORAGE_KEY_PREFIX}:${userId}`,
        );
    } catch {
        return null;
    }
}

export type ComposerPrefillSource =
    | 'learn_phoebe'
    | 'calendar_selection'
    | 'training_coverage'
    | 'coverage_bulk_ask_availability'
    | 'training_coverage_bulk_ask_availability';

type ComposerPrefillRequest = {
    readonly text: string;
    readonly source: ComposerPrefillSource;
    readonly onDelivered?: () => void;
    readonly newConversation?: boolean;
};

export type V3SidebarRunSelection = Readonly<{
    readonly mode: 'live' | 'sandbox';
    readonly openRequestId: number;
    readonly organizationId: string;
    readonly runId: string;
    readonly summary: AgentRunSummary;
}>;

type OpenChatOptions = Readonly<{
    readonly mode?: ChatPanelMode;
    readonly organizationId?: string | null;
    readonly runMode?: Mode;
}>;

type UIContextType = {
    readonly isChatVisible: boolean;
    readonly toggleChatVisibility: () => void;
    readonly showChat: () => void;
    readonly hideChat: () => void;
    readonly autoOpenChat: () => void;
    readonly closeAutoOpenedChat: () => void;
    readonly openChatWithId: (
        chatId: string,
        options?: OpenChatOptions,
    ) => void;
    readonly openSidebarChatWithId: (
        chatId: string,
        options?: OpenChatOptions,
    ) => void;
    readonly requestedChatId: string | null;
    readonly requestedChatOrganizationId: string | null;
    readonly requestedChatMode: ChatPanelMode | null;
    readonly clearRequestedChatId: () => void;
    readonly clearRequestedChatMode: () => void;
    readonly v3SidebarRunSelection: V3SidebarRunSelection | null;
    readonly clearV3SidebarRunSelection: () => void;
    readonly clearAutoOpenSuppression: () => void;
    readonly openNewChat: () => void;
    readonly newChatRequested: boolean;
    readonly clearNewChatRequest: () => void;
    readonly openChatWithComposerText: (
        text: string,
        source: ComposerPrefillSource,
        onDelivered?: () => void,
        options?: { readonly newConversation?: boolean },
    ) => void;
    readonly requestedComposerPrefill: ComposerPrefillRequest | null;
    readonly clearRequestedComposerPrefill: () => void;
    readonly openChatWithComposerDraft: (draft: ChatComposerDraft) => void;
    readonly requestedComposerDraft: ChatComposerDraft | null;
    readonly clearRequestedComposerDraft: () => void;
};

const UIContext = createContext<UIContextType>({
    isChatVisible: false,
    toggleChatVisibility: () => {},
    showChat: () => {},
    hideChat: () => {},
    autoOpenChat: () => {},
    closeAutoOpenedChat: () => {},
    openChatWithId: () => {},
    openSidebarChatWithId: () => {},
    requestedChatId: null,
    requestedChatOrganizationId: null,
    requestedChatMode: null,
    clearRequestedChatId: () => {},
    clearRequestedChatMode: () => {},
    v3SidebarRunSelection: null,
    clearV3SidebarRunSelection: () => {},
    clearAutoOpenSuppression: () => {},
    openNewChat: () => {},
    newChatRequested: false,
    clearNewChatRequest: () => {},
    openChatWithComposerText: () => {},
    requestedComposerPrefill: null,
    clearRequestedComposerPrefill: () => {},
    openChatWithComposerDraft: () => {},
    requestedComposerDraft: null,
    clearRequestedComposerDraft: () => {},
});

export function useUI() {
    return useContext(UIContext);
}

export function UIProvider({ children }: { children: ReactNode }) {
    const { session } = useSessionState();
    const [isChatVisible, setIsChatVisible] = useState(
        readPersistedChatVisible,
    );
    const [requestedChatId, setRequestedChatId] = useState<string | null>(null);
    const [requestedChatOrganizationId, setRequestedChatOrganizationId] =
        useState<string | null>(null);
    const [requestedComposerPrefill, setRequestedComposerPrefill] =
        useState<ComposerPrefillRequest | null>(null);
    const [requestedComposerDraft, setRequestedComposerDraft] =
        useState<ChatComposerDraft | null>(null);
    const [requestedChatMode, setRequestedChatMode] =
        useState<ChatPanelMode | null>(null);
    const [v3SidebarRunSelection, setV3SidebarRunSelection] =
        useState<V3SidebarRunSelection | null>(null);
    const clearV3SidebarRunSelection = useCallback(() => {
        setV3SidebarRunSelection((prev) => (prev === null ? prev : null));
    }, []);
    const [newChatRequested, setNewChatRequested] = useState(false);
    const wasAutoOpenedRef = useRef(false);
    const isChatVisibleRef = useRef(isChatVisible);
    const autoOpenSuppressedRef = useRef(false);
    const latestChatOpenRequestRef = useRef(0);
    const [resolveAgentChatDestination] = useState(
        createAgentChatDestinationResolver,
    );
    useEffect(() => {
        writePersistedChatVisible(isChatVisible);
    }, [isChatVisible]);

    const toggleChatVisibility = useCallback(() => {
        const nextChatVisibility = !isChatVisibleRef.current;
        wasAutoOpenedRef.current = false;
        // Respect a user-driven close by suppressing auto-open until they
        // explicitly reopen the chat.
        autoOpenSuppressedRef.current = !nextChatVisibility;
        isChatVisibleRef.current = nextChatVisibility;
        setIsChatVisible(nextChatVisibility);
    }, []);

    const showChat = useCallback(() => {
        wasAutoOpenedRef.current = false;
        autoOpenSuppressedRef.current = false;
        isChatVisibleRef.current = true;
        setIsChatVisible(true);
    }, []);

    const hideChat = useCallback(() => {
        wasAutoOpenedRef.current = false;
        autoOpenSuppressedRef.current = true;
        isChatVisibleRef.current = false;
        setIsChatVisible(false);
        // Drop any draft the composer never got to consume. Closing the panel
        // ends the request; otherwise it would sit here and surface unprompted
        // the next time a composer happens to mount.
        setRequestedComposerPrefill((prev) => (prev === null ? prev : null));
        setRequestedComposerDraft((prev) => (prev === null ? prev : null));
    }, []);

    const autoOpenChat = useCallback(() => {
        if (isChatVisibleRef.current || autoOpenSuppressedRef.current) {
            return;
        }
        wasAutoOpenedRef.current = true;
        isChatVisibleRef.current = true;
        setIsChatVisible(true);
    }, []);

    const closeAutoOpenedChat = useCallback(() => {
        if (!wasAutoOpenedRef.current) {
            return;
        }
        autoOpenSuppressedRef.current = true;
        wasAutoOpenedRef.current = false;
        isChatVisibleRef.current = false;
        setIsChatVisible(false);
    }, []);

    const requestChatWithId = useCallback(
        (
            chatId: string,
            options: OpenChatOptions | undefined,
            presentation: 'default' | 'sidebar',
        ) => {
            latestChatOpenRequestRef.current += 1;
            const requestNumber = latestChatOpenRequestRef.current;

            const openPanel = () => {
                if (autoOpenSuppressedRef.current) {
                    return;
                }
                clearV3SidebarRunSelection();
                setRequestedChatId((prev) => (prev === chatId ? prev : chatId));
                setRequestedChatOrganizationId(options?.organizationId ?? null);
                setRequestedChatMode(options?.mode ?? null);
                isChatVisibleRef.current = true;
                setIsChatVisible(true);
            };

            // UIProvider is mounted above OrganizationProvider, so the latter's
            // per-tab storage is the neutral source for callers that preserve
            // the public identifier-only call shape.
            const organizationId =
                options?.organizationId ??
                readSelectedOrganizationId(session?.identity.id ?? null);
            if (!organizationId) {
                openPanel();
                return;
            }

            const mode = options?.runMode ?? modeStore.getMode() ?? 'live';
            void resolveAgentChatDestination({
                chatId,
                organizationId,
                mode,
            })
                .then((resolution) => {
                    if (
                        requestNumber !== latestChatOpenRequestRef.current ||
                        resolution.kind === 'stale'
                    ) {
                        return;
                    }
                    if (resolution.destination.kind === 'panel') {
                        openPanel();
                        return;
                    }

                    if (presentation === 'sidebar') {
                        if (autoOpenSuppressedRef.current) {
                            return;
                        }
                        setRequestedChatId((prev) =>
                            prev === null ? prev : null,
                        );
                        setRequestedChatOrganizationId((prev) =>
                            prev === null ? prev : null,
                        );
                        setRequestedChatMode(options?.mode ?? null);
                        setRequestedComposerPrefill((prev) =>
                            prev === null ? prev : null,
                        );
                        setRequestedComposerDraft((prev) =>
                            prev === null ? prev : null,
                        );
                        setNewChatRequested((prev) => (prev ? false : prev));
                        setV3SidebarRunSelection({
                            mode,
                            openRequestId: requestNumber,
                            organizationId,
                            runId: resolution.destination.runId,
                            summary: resolution.destination.summary,
                        });
                        isChatVisibleRef.current = true;
                        setIsChatVisible(true);
                        return;
                    }

                    clearV3SidebarRunSelection();
                    const search = normalizeAgentChatSearch({
                        organizationId,
                        mode,
                    });
                    const query = new URLSearchParams();
                    if (search.organizationId !== undefined) {
                        query.set('organizationId', search.organizationId);
                    }
                    if (search.mode !== undefined) {
                        query.set('mode', search.mode);
                    }
                    const searchString = query.toString();
                    const path = `/agent/runs/${encodeURIComponent(
                        resolution.destination.runId,
                    )}`;
                    const nextUrl = searchString
                        ? `${path}?${searchString}`
                        : path;
                    window.history.replaceState(
                        window.history.state,
                        '',
                        nextUrl,
                    );
                    window.dispatchEvent(
                        new PopStateEvent('popstate', {
                            state: window.history.state,
                        }),
                    );
                })
                .catch(() => {
                    // Resolution failures leave the current panel state intact.
                });
        },
        [
            clearV3SidebarRunSelection,
            resolveAgentChatDestination,
            session?.identity.id,
        ],
    );

    const openChatWithId = useCallback(
        (chatId: string, options?: OpenChatOptions) => {
            requestChatWithId(chatId, options, 'default');
        },
        [requestChatWithId],
    );

    const openSidebarChatWithId = useCallback(
        (chatId: string, options?: OpenChatOptions) => {
            requestChatWithId(chatId, options, 'sidebar');
        },
        [requestChatWithId],
    );

    // Approval-toast clicks open the waiting agent chat through the module
    // registry. Registered here (not in the chat view) so the opener still
    // works while the chat panel is hidden and the view is unmounted. An
    // explicit click overrides a prior user-driven close, mirroring the
    // deep-link and sidebar-session paths.
    //
    // This site can't see the target chat's backend kind, so prefer the run
    // id: agent-run notifications target the run-native product chat, which
    // selects by run id. Fall back to the conversation id when no run id.
    useEffect(() => {
        return registerAgentConversationOpener(
            ({ agentConversationId, agentRunId, organizationId }) => {
                autoOpenSuppressedRef.current = false;
                openChatWithId(agentRunId ?? agentConversationId, {
                    organizationId,
                });
            },
        );
    }, [openChatWithId]);

    // Lets surfaces outside the chat (for example a scheduled outreach with no
    // linked conversation) open the docked panel on a fresh draft chat. The
    // chat view consumes newChatRequested and clears it.
    const openNewChat = useCallback(() => {
        latestChatOpenRequestRef.current += 1;
        setNewChatRequested(true);
        // Retire any pending open-specific-conversation request so it can't
        // race this one and land the user in an old thread.
        setRequestedChatId((prev) => (prev === null ? prev : null));
        setRequestedChatOrganizationId((prev) => (prev === null ? prev : null));
        setRequestedChatMode((prev) => (prev === null ? prev : null));
        setV3SidebarRunSelection((prev) => (prev === null ? prev : null));
        wasAutoOpenedRef.current = false;
        autoOpenSuppressedRef.current = false;
        isChatVisibleRef.current = true;
        setIsChatVisible(true);
    }, []);

    const clearNewChatRequest = useCallback(() => {
        setNewChatRequested((prev) => (prev ? false : prev));
    }, []);

    // Lets surfaces outside the chat (for example the sidebar get-started
    // checklist or the calendar selection pill) open the docked panel with a
    // draft already in the composer. The chat view consumes
    // requestedComposerPrefill and clears it; nothing is auto-sent.
    const openChatWithComposerText = useCallback(
        (
            text: string,
            source: ComposerPrefillSource,
            onDelivered?: () => void,
            options?: { readonly newConversation?: boolean },
        ) => {
            latestChatOpenRequestRef.current += 1;
            setRequestedComposerPrefill({
                text,
                source,
                onDelivered,
                newConversation: options?.newConversation,
            });
            setRequestedComposerDraft((prev) => (prev === null ? prev : null));
            // This targets the general chat, so retire any pending request to open
            // a specific conversation — otherwise a racing approval-toast click
            // would navigate to that thread and drop this draft into it.
            setRequestedChatId((prev) => (prev === null ? prev : null));
            setRequestedChatOrganizationId((prev) =>
                prev === null ? prev : null,
            );
            setRequestedChatMode((prev) => (prev === null ? prev : null));
            setV3SidebarRunSelection((prev) => (prev === null ? prev : null));
            wasAutoOpenedRef.current = false;
            autoOpenSuppressedRef.current = false;
            isChatVisibleRef.current = true;
            setIsChatVisible(true);
        },
        [],
    );

    const clearRequestedComposerPrefill = useCallback(() => {
        setRequestedComposerPrefill((prev) => (prev === null ? prev : null));
    }, []);

    // Activity-board handoffs carry both visible entity pills and the source
    // event IDs that the message endpoint links to the resulting agent run.
    const openChatWithComposerDraft = useCallback(
        (draft: ChatComposerDraft) => {
            latestChatOpenRequestRef.current += 1;
            setRequestedComposerDraft(draft);
            setRequestedComposerPrefill((prev) =>
                prev === null ? prev : null,
            );
            setRequestedChatId((prev) => (prev === null ? prev : null));
            setRequestedChatOrganizationId((prev) =>
                prev === null ? prev : null,
            );
            setRequestedChatMode((prev) => (prev === null ? prev : null));
            setV3SidebarRunSelection((prev) => (prev === null ? prev : null));
            wasAutoOpenedRef.current = false;
            autoOpenSuppressedRef.current = false;
            isChatVisibleRef.current = true;
            setIsChatVisible(true);
        },
        [],
    );

    const clearRequestedComposerDraft = useCallback(() => {
        setRequestedComposerDraft((prev) => (prev === null ? prev : null));
    }, []);

    const clearRequestedChatId = useCallback(() => {
        setRequestedChatId((prev) => (prev === null ? prev : null));
        setRequestedChatOrganizationId((prev) => (prev === null ? prev : null));
    }, []);

    const clearRequestedChatMode = useCallback(() => {
        setRequestedChatMode((prev) => (prev === null ? prev : null));
    }, []);

    const clearAutoOpenSuppression = useCallback(() => {
        autoOpenSuppressedRef.current = false;
    }, []);

    const contextValue = useMemo(
        () => ({
            isChatVisible,
            toggleChatVisibility,
            showChat,
            hideChat,
            autoOpenChat,
            closeAutoOpenedChat,
            openChatWithId,
            openSidebarChatWithId,
            requestedChatId,
            requestedChatOrganizationId,
            requestedChatMode,
            clearRequestedChatId,
            clearRequestedChatMode,
            v3SidebarRunSelection,
            clearV3SidebarRunSelection,
            clearAutoOpenSuppression,
            openNewChat,
            newChatRequested,
            clearNewChatRequest,
            openChatWithComposerText,
            requestedComposerPrefill,
            clearRequestedComposerPrefill,
            openChatWithComposerDraft,
            requestedComposerDraft,
            clearRequestedComposerDraft,
        }),
        [
            autoOpenChat,
            clearAutoOpenSuppression,
            clearNewChatRequest,
            clearRequestedChatId,
            clearRequestedComposerPrefill,
            clearRequestedComposerDraft,
            closeAutoOpenedChat,
            hideChat,
            isChatVisible,
            newChatRequested,
            openChatWithComposerText,
            openChatWithComposerDraft,
            openChatWithId,
            openSidebarChatWithId,
            openNewChat,
            requestedChatId,
            requestedChatOrganizationId,
            requestedChatMode,
            v3SidebarRunSelection,
            requestedComposerPrefill,
            requestedComposerDraft,
            showChat,
            toggleChatVisibility,
            clearRequestedChatMode,
            clearV3SidebarRunSelection,
        ],
    );

    return (
        <UIContext.Provider value={contextValue}>{children}</UIContext.Provider>
    );
}
